/**
 * Module-level Apollo client. Created exactly ONCE per page load.
 *
 * Why module-level?
 * RedwoodJS's RedwoodApolloProvider constructs `new ApolloClient(...)` inside
 * its render body without memoization, so any AuthContext value change (Clerk
 * session emit, setActive, currentUser ref churn) rebuilt the client and
 * wiped the cache. On the SO page this surfaced as "the page refreshes by
 * itself" — every useQuery briefly returned undefined, the Loading gate at
 * SalesOrderCell.tsx:124 flipped, and in-edit MUI grid state was destroyed.
 *
 * Owning the client at module scope side-steps the entire problem: auth is
 * read lazily per request via `Clerk.session.getToken()`, so the React tree
 * can rebuild as often as it wants without touching the client. This is the
 * canonical pattern for Vite SPAs documented across Clerk integration guides
 * (Hasura, Nhost, Grafbase).
 *
 * Sign-out clears the cache so the next user doesn't inherit it (clearStore,
 * not resetStore — see apollo-client#2774).
 *
 * File uploads go through Filestack (see lib/filestack), not GraphQL
 * multipart — plain HttpLink is enough.
 */

import {
  ApolloClient,
  ApolloProvider,
  createQueryPreloader,
  from,
  fromPromise,
  HttpLink,
  InMemoryCache,
  type Operation,
  useBackgroundQuery,
  useMutation,
  useQuery,
  useReadQuery,
  useSubscription,
  useSuspenseQuery,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import { useClerk } from '@clerk/react';
import type { LoadedClerk } from '@clerk/shared/types';
import { GraphQLHooksProvider } from '@redwoodjs/web';
// Two Redwood deps remain on this seam:
//   - GraphQLHooksProvider: keeps `useQuery from '@redwoodjs/web'` callers
//     working. Remove once those imports migrate to `@apollo/client`.
//   - fragmentRegistry: holds fragments registered by web/src/api/fragments
//     (e.g. OrganizationAttributes, ExpenseAttributes). InMemoryCache needs
//     this so queries that spread those fragments get expanded client-side
//     before the request goes to the server.
import { fragmentRegistry } from '@redwoodjs/web/apollo';
import { type ReactNode, useEffect } from 'react';

import { appErrorLink } from './apollo/appErrorLink';
import { clarityEventsLink } from './clarity-events';

declare global {
  // Set by Clerk's ClerkProvider once the SDK initializes. Always defined
  // before any GraphQL request fires from inside the auth-gated tree.
  interface Window {
    Clerk?: LoadedClerk;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Fetch a Clerk JWT, safe to call before Clerk has loaded. */
const getClerkToken = async (skipCache = false): Promise<string | null> => {
  try {
    return (await window.Clerk?.session?.getToken({ skipCache })) ?? null;
  } catch {
    return null;
  }
};

/** Headers for an authenticated request, or empty if no token. */
const authHeaders = (token: string | null): Record<string, string> =>
  token ? { 'auth-provider': 'clerk', authorization: `Bearer ${token}` } : {};

/** HTTP status pulled off Apollo's networkError (shape varies by transport). */
const statusOf = (err: ErrorResponse['networkError']): number | undefined => {
  const e = err as { statusCode?: number; status?: number } | null | undefined;
  return e?.statusCode ?? e?.status;
};

/** Track operations that already got a 401 retry. WeakSet auto-releases. */
const retriedOps = new WeakSet<Operation>();

// ─── Links ───────────────────────────────────────────────────────────────────

// `VITE_API_GRAPHQL_URL` is an override hook for per-env endpoints; it is not wired into any `.env*` today, so the `/.redwood/functions/graphql` fallback is what runs everywhere.
const graphQLUri = import.meta.env.VITE_API_GRAPHQL_URL ?? '/.redwood/functions/graphql';
const httpLink = new HttpLink({ uri: graphQLUri, credentials: 'include' });

const authLink = setContext(async (_op, { headers }) => {
  const token = await getClerkToken();
  return { headers: { ...headers, ...authHeaders(token) } };
});

/** Safety net when Clerk's proactive refresh (~50s before expiry) does not beat the next request — tab throttled, breakpoint, or clock skew. Forces a fresh token (`skipCache: true`) once per operation and replays it. Apollo recommends `onError` over `RetryLink` for auth: RetryLink only retries network errors. */
const errorLink = onError(({ networkError, operation, forward }) => {
  if (statusOf(networkError) !== 401 || retriedOps.has(operation)) return;
  retriedOps.add(operation);

  return fromPromise(getClerkToken(true)).flatMap((fresh) => {
    operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
      headers: { ...headers, ...authHeaders(fresh) },
    }));
    return forward(operation);
  });
});

// ─── Client ──────────────────────────────────────────────────────────────────

export const apolloClient = new ApolloClient({
  // clarityEventsLink first → tags every operation with a Microsoft Clarity
  // event before the auth/HTTP layers fire. Keeps mutation-funnel visibility
  // we had under RedwoodApolloProvider.
  //
  // appErrorLink sits ABOVE the 401 `errorLink` (TAS-3247): errors flow back up
  // the chain, so it observes the FINAL post-retry response — a transient 401
  // that recovers never toasts. It surfaces typed AppError `graphQLErrors`, and
  // (behind the APP_ERROR flag, throttled) a terminal `networkError` that the
  // 401 `errorLink` only replays but never toasts.
  link: from([clarityEventsLink, appErrorLink, errorLink, authLink, httpLink]),
  cache: new InMemoryCache({ fragments: fragmentRegistry }),
  defaultOptions: {
    // Serve cached data instantly + refresh from the server in the background.
    // Callers needing fresh data can override per query.
    watchQuery: { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true },
  },
});

/** Starts a query outside React; route loaders hand the returned ref to the page (routing/routePreload.ts). */
export const preloadQuery = createQueryPreloader(apolloClient);

/** The Clerk session and organization a request runs under, or undefined unless the session is active with an org. */
export function activeClerkScope(): { sessionId: string; organizationId: string } | undefined {
  const session = window.Clerk?.session;
  const organizationId = window.Clerk?.organization?.id;
  if (!session || session.status !== 'active' || !organizationId) return undefined;
  return { sessionId: session.id, organizationId };
}

const apolloHooks = { useQuery, useMutation, useSubscription, useSuspenseQuery, useBackgroundQuery, useReadQuery };

// ─── Provider ────────────────────────────────────────────────────────────────

export function AgrifulApolloProvider({ children }: { children: ReactNode }) {
  // useClerk() inside the effect avoids the "Clerk not loaded yet" race that `globalThis.Clerk` would hit on first render. clearStore() is idempotent, so signed-in state needs no tracking.
  const clerk = useClerk();
  useEffect(() => {
    return clerk.addListener((resources) => {
      if (!resources.session) apolloClient.clearStore().catch(() => {});
    });
  }, [clerk]);

  return (
    <ApolloProvider client={apolloClient}>
      <GraphQLHooksProvider {...apolloHooks}>{children}</GraphQLHooksProvider>
    </ApolloProvider>
  );
}
